/**
 * Company chat (owner, 20 Oct 2026): what the screens read from /chat, and the
 * unread count the menu shows. The chat belongs to no book, so every call is
 * sent with `{ book: null }` — the same rooms whichever book the header is on.
 *
 * There is no live socket: an open room asks for what is new or changed every
 * few seconds, the inbox a little slower, and the menu's count slower still.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatsInCompany, type ChatInbox, type ChatPerson } from '@bright/shared';

import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/** How often an open room, the room list and the menu's count ask again. */
export const CHAT_POLL_ROOM_MS = 3_000;
export const CHAT_POLL_INBOX_MS = 12_000;
export const CHAT_POLL_UNREAD_MS = 30_000;

/** Every chat call is made from the sign-in's own book, whichever book the header shows. */
export const NO_BOOK = { book: null } as const;

export const chatKeys = {
  all: ['chat'] as const,
  unread: ['chat', 'unread'] as const,
  inbox: ['chat', 'inbox'] as const,
  people: (q: string) => ['chat', 'people', q] as const,
  room: (roomId: string) => ['chat', 'room', roomId] as const,
};

/** Whether this sign-in is in the chat at all. */
export function useChatMember(): boolean {
  const { user } = useAuth();
  // Another desk of a person (20 Oct 2026) is not in the chat: the person is, on their own sign-in.
  return Boolean(user) && chatsInCompany({ roleKey: user!.role.key, email: user!.email, seatOfId: user!.seatOf?.id });
}

/** The menu's count: unread messages across every room. */
export function useChatUnread() {
  const member = useChatMember();
  return useQuery({
    queryKey: chatKeys.unread,
    queryFn: () => api.get<{ total: number }>('/chat/unread', undefined, { book: null }),
    enabled: member,
    staleTime: 10_000,
    refetchInterval: CHAT_POLL_UNREAD_MS,
    refetchOnWindowFocus: true,
  });
}

/** The room list: the company room first, then private rooms, newest first. */
export function useChatInbox(enabled = true) {
  const member = useChatMember();
  return useQuery({
    queryKey: chatKeys.inbox,
    queryFn: () => api.get<ChatInbox>('/chat/inbox', undefined, { book: null }),
    enabled: member && enabled,
    staleTime: 5_000,
    refetchInterval: CHAT_POLL_INBOX_MS,
    refetchOnWindowFocus: true,
  });
}

/**
 * The colleagues one can start a chat with or give kudos to: employees in the
 * chat, active, the viewer left out. The server answers a plain list; a
 * wrapped `{ data }` is read the same way.
 */
export function useChatPeople(q: string, enabled = true) {
  const member = useChatMember();
  const term = q.trim();
  return useQuery({
    queryKey: chatKeys.people(term),
    queryFn: async () => {
      const body = await api.get<ChatPerson[] | { data: ChatPerson[] }>('/chat/people', term ? { q: term } : undefined, NO_BOOK);
      return Array.isArray(body) ? body : body.data;
    },
    enabled: member && enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** Opens the private room with one colleague, making it the first time. */
export function useOpenDirect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post<{ roomId: string }>('/chat/direct', { userId }, NO_BOOK),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.inbox });
    },
  });
}
